import { CheckIcon } from "@heroicons/react/outline";
import { compact, get, isEmpty, map, partition, values } from "lodash";
import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Multiselect } from "react-widgets";
import { useLabels } from "../../context/labels";
import classNames from "../../utils/classNames";
import { Entity } from "../../utils/constants";
import { Label } from "../../utils/types";
import CloseIcon from "../icons/CloseIcon";

interface Props {
  className?: string;
  entity: Entity;
  linkedUuid: string;
  onClose: () => void;
}

const EditLabels: React.FC<Props> = ({
  className,
  entity,
  linkedUuid,
  onClose
}) => {
  const { labels, linkedLabels, addBulkItemLabels, updateItemLabels } =
    useLabels();
  const [dummySelectdLabels, setDummySelectdLabels] = useState<Label[]>([]);
  const [loading, setLoading] = useState(false);

  const selectedLabels = useMemo(
    () =>
      compact(
        get(linkedLabels, linkedUuid, []).map(
          (labelUuid: string) => labels[labelUuid]
        )
      ),

    [labels, linkedLabels, linkedUuid]
  );

  const labelOptions = useMemo(
    () =>
      values(labels)
        .filter(
          (label) =>
            label.type === entity && !dummySelectdLabels.includes(label)
        )
        .sort((a, b) => a.order - b.order),
    [entity, labels, dummySelectdLabels]
  );

  const hasChanges = useMemo(
    () => JSON.stringify(selectedLabels) !== JSON.stringify(dummySelectdLabels),
    [dummySelectdLabels, selectedLabels]
  );

  const createItemLabel = useCallback(
    (name: string) => {
      setDummySelectdLabels((prevState) => [
        ...prevState,
        {
          uuid: "",
          workspaceUuid: "",
          type: entity,
          name,
          color: "",
          order: 0
        }
      ]);
    },
    [entity]
  );

  const saveChanges = useCallback(async () => {
    setLoading(true);

    const data = partition(dummySelectdLabels, (label) => label?.uuid === ""); // [new, existing]

    await updateItemLabels(entity, linkedUuid, map(data[1], "uuid"));

    if (!isEmpty(data[0])) {
      await addBulkItemLabels(entity, [linkedUuid], map(data[0], "name"));
    }

    setLoading(false);
    onClose();
  }, [
    addBulkItemLabels,
    dummySelectdLabels,
    entity,
    linkedUuid,
    onClose,
    updateItemLabels
  ]);

  const changeItemLabel = useCallback((newSelectedLabels: Label[]) => {
    setDummySelectdLabels(newSelectedLabels);
  }, []);

  useEffect(() => {
    setDummySelectdLabels(selectedLabels);
  }, [selectedLabels]);

  return (
    <div className="flex-1 flex items-center">
      <Multiselect
        containerClassName={classNames(
          "dark:bg-gray-700 dark:text-white",
          className
        )}
        busy={loading}
        placeholder="Add labels"
        data={labelOptions}
        value={dummySelectdLabels}
        allowCreate="onFilter"
        onCreate={createItemLabel}
        onChange={(newSelectedLabels) => {
          changeItemLabel(newSelectedLabels);
        }}
        textField="name"
        filter="contains"
        defaultOpen
        messages={{
          createOption: (_value: any, searchTerm: string) =>
            `Add label: ${searchTerm}`
        }}
        dropUp
      />
      <button
        type="button"
        onClick={() => {
          if (!hasChanges) return;

          saveChanges();
        }}
        className={classNames(
          "inline-flex justify-center p-2 text-sm -mt-1 ml-3 font-medium rounded-md text-gray-700 hover:bg-gray-50 dark:text-white dark:hover:text-gray-800",
          !hasChanges && "opacity-50 cursor-not-allowed"
        )}
      >
        <CheckIcon className="h-5 w-5" />
      </button>

      <button
        type="button"
        onClick={onClose}
        className="inline-flex justify-center p-2 -mt-1 ml-1 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50 dark:text-white dark:hover:text-gray-800"
      >
        <CloseIcon className="h-5 w-5" />
      </button>
    </div>
  );
};

export default EditLabels;
